import { useCallback, useMemo, useState, useEffect } from 'react'

import { ModalDocuments, ModalDocumentsTips } from 'modules/documents'
import { useZaiChatObserver } from 'modules/zai/common/contexts'
import { FeatureFlagsStore } from 'stores'
import { analyticsTracker } from 'utils'

import { DocumentsModalsContext } from './context'
import type { TDocumentsModalsModalDocumentsConfig, TDocumentsModalsModalDocumentsTipsConfig } from './types'

export function DocumentsModalsProvider({ children }: { children: React.ReactNode }) {
  const isZaiRagEnabled = FeatureFlagsStore.getFeatureFlag('isZaiRagEnabled')
  const { zaiChatIframeRef, registerModalCallbacks } = useZaiChatObserver()

  const [modalDocuments, setModalDocuments] = useState<TDocumentsModalsModalDocumentsConfig>({
    isOpen: false,
  })
  const [modalDocumentsTips, setModalDocumentsTips] = useState<TDocumentsModalsModalDocumentsTipsConfig>({
    isOpen: false,
  })

  const openDocumentsModal = useCallback(() => {
    setModalDocuments({ isOpen: true })
  }, [])

  const openDocumentsTipsModal = useCallback(() => {
    const { FIL_OPEN_ABOUT } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(FIL_OPEN_ABOUT)

    setModalDocumentsTips({ isOpen: true })
  }, [])

  // Register modal callbacks with ZaiChatObserver
  useEffect(() => {
    registerModalCallbacks({
      onModalDocumentsOpen: openDocumentsModal,
      onModalDocumentsTipsOpen: openDocumentsTipsModal,
    })
  }, [registerModalCallbacks, openDocumentsModal, openDocumentsTipsModal])

  const closeDocumentsModal = useCallback(() => {
    setModalDocuments({ isOpen: false })
  }, [])

  const closeDocumentsTipsModal = useCallback(() => {
    setModalDocumentsTips({ isOpen: false })
  }, [])

  const handleDocumentsModalTipsRedirect = useCallback(() => {
    openDocumentsModal()
    closeDocumentsTipsModal()
  }, [openDocumentsModal, closeDocumentsTipsModal])

  const handleDocumentsModalRedirect = useCallback(() => {
    closeDocumentsModal()
    openDocumentsTipsModal()
  }, [closeDocumentsModal, openDocumentsTipsModal])

  const value = useMemo(
    () => ({
      zaiChatIframeRef,
      modalDocuments,
      modalDocumentsTips,
      openDocumentsModal,
      openDocumentsTipsModal,
      closeDocumentsModal,
      closeDocumentsTipsModal,
      handleDocumentsModalTipsRedirect,
      handleDocumentsModalRedirect,
    }),
    [
      zaiChatIframeRef,
      closeDocumentsModal,
      closeDocumentsTipsModal,
      handleDocumentsModalRedirect,
      handleDocumentsModalTipsRedirect,
      modalDocuments,
      modalDocumentsTips,
      openDocumentsModal,
      openDocumentsTipsModal,
    ]
  )

  return (
    <DocumentsModalsContext.Provider value={value}>
      {children}

      {isZaiRagEnabled && (
        <>
          <ModalDocuments {...modalDocuments} onClose={closeDocumentsModal} />
          <ModalDocumentsTips
            {...modalDocumentsTips}
            handleOnClose={closeDocumentsTipsModal}
            handleOnUpload={handleDocumentsModalTipsRedirect}
          />
        </>
      )}
    </DocumentsModalsContext.Provider>
  )
}
